import { useCallback, useEffect, useState } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { Patient, Procedure } from '@medplum/fhirtypes';
import { isTherapeuticService } from '@/utils/procedure.utils';

export interface ProceduresData {
  readonly procedures: Procedure[];
  readonly therapeuticInterventions: Procedure[];
}

export const useProceduresData = (patient?: Patient, enabled = true) => {
  const medplum = useMedplum();
  const [data, setData] = useState<ProceduresData>({
    procedures: [],
    therapeuticInterventions: [],
  });
  const [loading, setLoading] = useState(false);

  const fetchProcedures = useCallback(async () => {
    if (!patient?.id) {
      setData({ procedures: [], therapeuticInterventions: [] });
      return;
    }

    medplum.invalidateSearches('Procedure');

    setLoading(true);
    try {
      const ref = `Patient/${patient.id}`;
      const searchMeta = { _count: 100, _sort: '-_lastUpdated' };

      const allProcedures = await medplum.searchResources('Procedure', {
        subject: ref,
        ...searchMeta,
      });

      // Separate therapeutic interventions from regular procedures
      const { therapeuticInterventions, procedures } = allProcedures.reduce(
        (acc, procedure) => {
          if (isTherapeuticService(procedure)) {
            acc.therapeuticInterventions.push(procedure);
          } else {
            acc.procedures.push(procedure);
          }
          return acc;
        },
        {
          therapeuticInterventions: [] as Procedure[],
          procedures: [] as Procedure[],
        }
      );

      setData({ procedures, therapeuticInterventions });
    } catch (error) {
      console.error('Error loading procedures:', error);
    } finally {
      setLoading(false);
    }
  }, [patient?.id, medplum]);

  useEffect(() => {
    if (!enabled) {
      setData({ procedures: [], therapeuticInterventions: [] });
      return;
    }
    fetchProcedures();
  }, [fetchProcedures, enabled]);

  useSubscription(
    enabled && patient?.id ? `Procedure?subject=Patient/${patient.id}` : '',
    () => {
      fetchProcedures();
    }
  );

  return { data, loading, refetch: fetchProcedures };
};
